import { Select as SelectPrimitive } from '@base-ui/react/select';

import ChevronDownIcon from '@/assets/svg/chevron-down.svg?react';
import { Spinner } from '@/components/loaders';
import { cn } from '@/lib/utils';

import { pickerTriggerIconSizeClasses } from '../pickerPrimitiveStyles';
import { useSelectContext } from './SelectContext';

const selectControlSizeClasses = {
  small: 'py-px text-sm',
  medium: 'py-[5px] text-sm',
  large: 'py-[7px] text-base',
};

export type SelectTriggerProps = SelectPrimitive.Trigger.Props & {
  'aria-warning'?: boolean;
};

const SelectTrigger = ({
  className,
  'aria-warning': warning,
  children,
  ...props
}: SelectTriggerProps) => {
  const { size, isLoading } = useSelectContext();

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      data-warning={warning ? 'true' : undefined}
      data-loading={isLoading ? 'true' : undefined}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-1.5 rounded-xs border bg-white pr-2 pl-3 text-sm whitespace-nowrap transition-colors outline-none select-none',
        'border-gray-600 bg-white text-gray-900 ring-offset-0 data-placeholder:text-gray-500',
        '*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0',

        'aria-expanded:border-gray-900 aria-expanded:[&_svg]:rotate-180',

        'hover:border-gray-900',

        'focus:border-green-500 focus:ring-1 focus:ring-green-500/50',

        'aria-invalid:border-red-400',
        'aria-invalid:focus:border-red-500 aria-invalid:focus:ring-red-500/50',
        'aria-invalid:hover:border-red-500',

        'data-warning:border-yellow-400 data-warning:hover:border-yellow-500 data-warning:focus:border-yellow-500 data-warning:focus:ring-yellow-500/50',

        'disabled:cursor-not-allowed disabled:border-gray-400 disabled:bg-gray-100 disabled:text-gray-500 disabled:[&_svg]:text-gray-500',

        selectControlSizeClasses[size],
        pickerTriggerIconSizeClasses[size],
        className,
      )}
      {...props}
    >
      {children}
      {isLoading ? (
        <Spinner className="pointer-events-none" />
      ) : (
        <SelectPrimitive.Icon
          render={
            <ChevronDownIcon
              className={cn('pointer-events-none text-gray-900 transition-transform duration-100')}
            />
          }
        />
      )}
    </SelectPrimitive.Trigger>
  );
};

export default SelectTrigger;
